/* eslint-disable no-case-declarations */
import { ArrowUpward } from '@mui/icons-material';
import { Box, Checkbox, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setTransportItem } from 'app/actions/issue';
import {
  addTransportCheckedId,
  deleteTransportCheckedItem,
  setTransportCheckedItem,
  setTransportMarkedCheckedItem,
  setTransportSelectedItem,
  setTransportSortSelectedItem,
} from 'app/actions/map';
import { Autosizer, Protect, Scrollbar } from 'app/components';
import {
  TransportCircleIcon,
  TransportConditionSignalOffIcon,
  TransportFuelIcon,
  TransportParkingIcon,
  TransportSensorIcon,
  TransportSquareIcon,
  TransportTriangleIcon,
} from 'app/icons';
import { MapTransportConditionIconsList } from 'app/pages/Map';
import { useInterval, useLocalStorage, useSingleAndDoubleClick } from 'app/services';
import React, { createContext, createRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

import { translitToRus } from '../../utils';

const mapTransportIcons = {
  TransportFuelIcon: TransportFuelIcon,
  TransportParkingIcon: TransportParkingIcon,
  TransportSensorIcon: TransportSensorIcon,
  TransportConditionSignalOffIcon: TransportConditionSignalOffIcon,
};

const MapFilterTransportListContext = createContext();

const MapTransportIcon = ({ name }) => {
  const Component = mapTransportIcons[name];

  if (Component) {
    return <Component />;
  }

  return <React.Fragment />;
};

const MapFilterTransportList = () => {
  const dispatch = useDispatch();
  const isResizingColumn = useRef(null);
  const transportCheckedItems = useSelector((state) =>
    state.map.transportFilterCheckedType === 'filter' ? state.map.transportProcessingCheckedItems : state.map.transportCheckedItems
  );
  const {
    ref: mapRef,
    transportItems,
    transportFilterCheckedItems,
    transportSortSelectedItem,
    transportSelectedItem,
    transportColumnCheckedItems,
    transportSearchValue,
    transportCheckedItems: transportCheckedItemsAll,
    transportProcessingCheckedItems,
    transportFilterCheckedType,
    transportMarkedCheckedItems,
    isHistoryOpen,
  } = useSelector((state) => state.map);
  const { handleItemClick, handleItemDoubleClick } = useSingleAndDoubleClick({
    onClick: (v) => handleItemClickEvent(v),
    onDoubleClick: (v) => handleItemDoubleClickEvent(v),
  });
  const [, setTransportCheckedStorageItems] = useLocalStorage('transportCheckedItems', []);
  const [, setTransportMarkedCheckedStorageItems] = useLocalStorage('transportMarkedCheckedItems', []);
  const [, setTransportProcessingCheckedStorageItems] = useLocalStorage('transportProcessingCheckedItems', []);
  const [, setTransportSortCheckedStorageItems] = useLocalStorage('transportSortSelectedItem', {});
  const [transportColumnsWidth, setTransportColumnsWidth] = useLocalStorage('transportColumnsWidth', {});
  const [datetimeInterval, setDatetimeInterval] = useState(moment());

  const listRef = useRef(null);

  const columns = useMemo(
    () =>
      config.mapTransportColumns
        .filter((column) => transportColumnCheckedItems.includes(column.name))
        .map((column) => ({
          ...column,
          width: transportColumnsWidth[column.name] ? transportColumnsWidth[column.name] : column.width ? column.width : column.minWidth,
        })),
    [transportColumnsWidth, transportColumnCheckedItems]
  );

  const columnsWatch = useRef(columns);
  const columnsRefs = useRef(Object.fromEntries(config.mapTransportColumns.map((column) => [column.name, createRef()])));

  const sortItems = useMemo(() => {
    const compareFn = (a, b) => {
      switch (transportSortSelectedItem.name) {
        case 'name':
          switch (transportSortSelectedItem.order) {
            case 'ASC':
              return a.name.localeCompare(b.name, {
                sensitivity: 'base',
              });
            case 'DESC':
              return b.name.localeCompare(a.name, {
                sensitivity: 'base',
              });
          }
          break;
        case 'condition':
          switch (transportSortSelectedItem.order) {
            case 'ASC':
              return moment(b.condition) - moment(a.condition);
            case 'DESC':
              return moment(a.condition) - moment(b.condition);
          }
          break;
        case 'geofence':
          switch (transportSortSelectedItem.order) {
            case 'ASC':
              return b.geofence_id === config.conditions.geofence_id.default
                ? -1
                : a.geofence_id === config.conditions.geofence_id.default
                  ? 1
                  : a.geofence_name.localeCompare(b.geofence_name, { sensitivity: 'base' }) ||
                    a.name.localeCompare(b.name, {
                      sensitivity: 'base',
                    });
            case 'DESC':
              return b.geofence_id === config.conditions.geofence_id.default
                ? -1
                : a.geofence_id === config.conditions.geofence_id.default
                  ? 1
                  : b.geofence_name.localeCompare(a.geofence_name, { sensitivity: 'base' }) ||
                    b.name.localeCompare(a.name, {
                      sensitivity: 'base',
                    });
          }
          break;
        case 'parking_time':
        case 'sensor_count':
          switch (transportSortSelectedItem.order) {
            case 'ASC':
              return b[transportSortSelectedItem.name] === config.conditions[transportSortSelectedItem.name].default
                ? -1
                : a[transportSortSelectedItem.name] === config.conditions[transportSortSelectedItem.name].default
                  ? 1
                  : a[transportSortSelectedItem.name] - b[transportSortSelectedItem.name];
            case 'DESC':
              return b[transportSortSelectedItem.name] === config.conditions[transportSortSelectedItem.name].default
                ? -1
                : a[transportSortSelectedItem.name] === config.conditions[transportSortSelectedItem.name].default
                  ? 1
                  : b[transportSortSelectedItem.name] - a[transportSortSelectedItem.name];
          }
          break;
        case 'signal_off':
        case 'guard_status_sum':
        case 'total_mileage_avg':
          switch (transportSortSelectedItem.order) {
            case 'ASC':
              return b[transportSortSelectedItem.name] === null
                ? -1
                : a[transportSortSelectedItem.name] === null
                  ? 1
                  : a[transportSortSelectedItem.name] - b[transportSortSelectedItem.name];
            case 'DESC':
              return b[transportSortSelectedItem.name] === null
                ? -1
                : a[transportSortSelectedItem.name] === null
                  ? 1
                  : b[transportSortSelectedItem.name] - a[transportSortSelectedItem.name];
          }
          break;
        case 'last_activity_date':
          switch (transportSortSelectedItem.order) {
            case 'ASC':
              return b[transportSortSelectedItem.name] === null
                ? -1
                : a[transportSortSelectedItem.name] === null
                  ? 1
                  : Math.round(moment(datetimeInterval).diff(moment(a[transportSortSelectedItem.name])) / 1000 / 60) -
                    Math.round(moment(datetimeInterval).diff(moment(b[transportSortSelectedItem.name])) / 1000 / 60);
            case 'DESC':
              return b[transportSortSelectedItem.name] === null
                ? -1
                : a[transportSortSelectedItem.name] === null
                  ? 1
                  : Math.round(moment(datetimeInterval).diff(moment(b[transportSortSelectedItem.name])) / 1000 / 60) -
                    Math.round(moment(datetimeInterval).diff(moment(a[transportSortSelectedItem.name])) / 1000 / 60);
          }
          break;
      }
    };
    let items = transportItems.reduce((accumulator, currentValue) => {
      if (accumulator.findIndex((item) => item.id === currentValue.id) !== -1) {
        return accumulator;
      }

      // Если включен фильтр «Активность».
      if (transportFilterCheckedItems.indexOf('activity') !== -1) {
        // Исключаем записи с временной меткой превышающей лимит.
        if (
          !currentValue.last_activity_date ||
          moment(datetimeInterval).diff(moment(currentValue.last_activity_date)) < config.activityTimeout
        ) {
          return accumulator;
        }
      }

      // Установим дополнительные параметры для отображения сортировки.
      currentValue = {
        ...currentValue,
        ...{
          datetime: '',
          geofence_name: 'NA',
          geofence_id: config.conditions.geofence_id.default,
          guard_status: config.conditions.guard_status.default,
          parking_time: config.conditions.parking_time.default,
          sensor_count: config.conditions.sensor_count.default,
          guard_status_sum: null,
          signal_off: null,
          condition: null,
        },
      };

      if (currentValue.conditions.length === 0) {
        return transportFilterCheckedItems.indexOf('signal') !== -1 ? [...accumulator, currentValue] : accumulator;
      }

      // Преобразуем исходное десятичное число к двоичному числу, помещенному в массив [9 8765 4321] -> guard_out9...guard_out1.
      // Получим сумму значений всех dut, обращаясь к каждому как к dutN.
      const guard_out = (currentValue.conditions[currentValue.conditions.length - 1].data.guard_out >>> 0).toString(2).padStart(9, '0');
      const guard_status = (currentValue.conditions[currentValue.conditions.length - 1].data.guard_status >>> 0)
        .toString(2)
        .padStart(9, '0');

      currentValue.datetime = currentValue.conditions[currentValue.conditions.length - 1].datetime;
      currentValue.geofence_id = currentValue.conditions[currentValue.conditions.length - 1].data.geofence_id;
      currentValue.geofence_name = currentValue.conditions[currentValue.conditions.length - 1].data.geofence_name;
      currentValue.guard_status = (currentValue.conditions[currentValue.conditions.length - 1].data.guard_status >>> 0)
        .toString(2)
        .padStart(9, '0');
      currentValue.guard_status_sum = config.numberOfTank
        .map((number) => currentValue.conditions[currentValue.conditions.length - 1].data[`dut${number}`])
        .filter((number) => number !== config.conditions.dut.default)
        .reduce((a, b) => a + b, 0);
      currentValue.total_mileage_avg = currentValue.conditions[currentValue.conditions.length - 1].data.total_mileage_avg;
      currentValue.parking_time = currentValue.conditions[currentValue.conditions.length - 1].data.parking_time;
      currentValue.sensor_count = currentValue.conditions[currentValue.conditions.length - 1].data.sensor_count;
      currentValue.signal_off = Math.max(
        0,
        moment(datetimeInterval).diff(moment(currentValue.conditions[currentValue.conditions.length - 1].datetime).endOf('minute'))
      );
      currentValue.condition = Math.max(
        ...currentValue.conditions[currentValue.conditions.length - 1].filters
          .filter((filter) => transportFilterCheckedItems.indexOf(filter.name) !== -1)
          .map((filter) => new Date(filter.datetime))
      );

      // Принудительный фильтр для отображения в списке.
      // Общее количество фильтров за исключением активности.
      if (transportFilterCheckedItems.length >= config.mapTransportFilters.length - 1) {
        return [...accumulator, currentValue];
      }

      // Если включен фильтр «Охрана».
      if (transportFilterCheckedItems.indexOf('guard') !== -1) {
        if (currentValue.conditions[currentValue.conditions.length - 1].data.guard_out !== config.conditions.guard_out.default) {
          // Проверить guard_out8
          switch (guard_out[1]) {
            case '1':
              return [...accumulator, currentValue];
          }
        }
      }

      // Если включен фильтр «Тревога».
      if (transportFilterCheckedItems.indexOf('alarm') !== -1) {
        if (currentValue.conditions[currentValue.conditions.length - 1].data.guard_status !== config.conditions.guard_status.default) {
          // Проверить guard_status8
          switch (guard_status[1]) {
            case '1':
              return [...accumulator, currentValue];
          }
        }
      }

      // Если включен фильтр «Слив».
      if (transportFilterCheckedItems.indexOf('drain') !== -1) {
        if (currentValue.conditions[currentValue.conditions.length - 1].data.geofence_name !== 'NA') {
          switch (currentValue.conditions[currentValue.conditions.length - 1].data.geofence_type) {
            case 1:
              // Проверить guard_status7
              switch (guard_status[2]) {
                case '1':
                  return [...accumulator, currentValue];
              }
          }
        }
      }

      // Если включен фильтр «Налив».
      if (transportFilterCheckedItems.indexOf('pouring') !== -1) {
        if (currentValue.conditions[currentValue.conditions.length - 1].data.geofence_name !== 'NA') {
          switch (currentValue.conditions[currentValue.conditions.length - 1].data.geofence_type) {
            case 3:
              return [...accumulator, currentValue];
          }
        }
      }

      // Если включен фильтр «Парковка».
      if (transportFilterCheckedItems.indexOf('parking') !== -1) {
        switch (currentValue.conditions[currentValue.conditions.length - 1].data.is_parked) {
          case 1:
            return [...accumulator, currentValue];
        }
      }

      // Если включен фильтр «Движение».
      if (transportFilterCheckedItems.indexOf('speed') !== -1) {
        switch (currentValue.conditions[currentValue.conditions.length - 1].data.is_parked) {
          case 0:
            return [...accumulator, currentValue];
        }
      }

      // Если включен фильтр «Потеря связи».
      if (transportFilterCheckedItems.indexOf('signal') !== -1) {
        const diff = Math.max(
          0,
          moment().diff(
            moment
              .utc(currentValue.conditions[currentValue.conditions.length - 1].data.datetime)
              .local()
              .endOf('minute')
          )
        );

        if (diff > config.signalOffTimeout[0].interval[1]) {
          return [...accumulator, currentValue];
        }
      }

      // Если включен фильтр «Стоянка от 10 минут».
      if (transportFilterCheckedItems.indexOf('parking_time') !== -1) {
        if (currentValue.conditions[currentValue.conditions.length - 1].data.geofence_name === 'NA') {
          switch (currentValue.conditions[currentValue.conditions.length - 1].data.parking_overtime) {
            case 1:
              switch (true) {
                case currentValue.conditions[currentValue.conditions.length - 1].data.parking_time <= 21600:
                  return [...accumulator, currentValue];
              }
          }
        }
      }

      return accumulator;
    }, []);
    if (isHistoryOpen) {
      items = items.filter((item) => transportCheckedItems.includes(item.id));
    }

    return items.sort(compareFn);
  }, [transportItems, transportFilterCheckedItems, transportSortSelectedItem, datetimeInterval]);

  const searchItems = useMemo(() => {
    if (transportSearchValue.length) {
      return sortItems.filter((item) => {
        if (transportSortSelectedItem.name === 'geofence') {
          return (
            translitToRus(item.name).indexOf(translitToRus(transportSearchValue)) !== -1 ||
            translitToRus(item.geofence_name).indexOf(translitToRus(transportSearchValue)) !== -1
          );
        }

        return translitToRus(item.name).indexOf(translitToRus(transportSearchValue)) !== -1;
      });
    }
    return sortItems;
  }, [sortItems, transportSearchValue]);

  const handleItemClickEvent = (value) => {
    // const activity = require('app/services/activity').default;
    // activity.logEntitySelect();
    dispatch(setTransportSelectedItem(value));
    dispatch(setTransportItem(value));

    if (value.conditions.length && value.conditions[value.conditions.length - 1].data.geodata !== 'ERR') {
      mapRef.current.handleClosePopup();
      mapRef.current.handleFlyTo(
        [value.conditions[value.conditions.length - 1].data.lat, value.conditions[value.conditions.length - 1].data.lon],
        null
      );
    }
  };

  const handleItemDoubleClickEvent = (value) => {
    // const activity = require('app/services/activity').default;
    // activity.logUiInteraction();
    if (transportCheckedItems.indexOf(value.id) !== -1) {
      dispatch(addTransportCheckedId(value.id));
    }

    if (value.conditions.length && value.conditions[value.conditions.length - 1].data.geodata !== 'ERR') {
      mapRef.current.handleClosePopup();
      mapRef.current.handleFlyTo(
        [value.conditions[value.conditions.length - 1].data.lat, value.conditions[value.conditions.length - 1].data.lon],
        config.mapMaxZoom
      );
    }
  };

  const handleSortClick = (value) => dispatch(setTransportSortSelectedItem(value));

  const handleCheckboxClick = (value) => (event) => {
    event.preventDefault();
    event.stopPropagation();

    const notChecked = transportCheckedItems.indexOf(value.id) === -1;

    if (notChecked) {
      dispatch(addTransportCheckedId(value.id));
    } else {
      dispatch(deleteTransportCheckedItem(value.id));
    }

    if (notChecked && value.conditions.length && value.conditions[value.conditions.length - 1].data.geodata !== 'ERR') {
      mapRef.current.handleClosePopup();
      mapRef.current.handleFlyTo(
        [value.conditions[value.conditions.length - 1].data.lat, value.conditions[value.conditions.length - 1].data.lon],
        null
      );
    }
  };

  const handleCheckboxHeadClick = (e) => {
    const newIds = searchItems.map((item) => item.id);

    batch(() => {
      if (e.target.checked) {
        const checkedItems = Array.from(new Set([...transportCheckedItems, ...newIds]));
        dispatch(setTransportCheckedItem(checkedItems));
        dispatch(setTransportMarkedCheckedItem(transportMarkedCheckedItems.filter((item) => !checkedItems.includes(item))));
      } else {
        const showItems = new Set(newIds);
        const checkedItems = transportCheckedItems.filter((id) => !showItems.has(id));
        const markedItems = Array.from(new Set([...transportMarkedCheckedItems, ...newIds]));
        dispatch(setTransportCheckedItem(checkedItems));
        dispatch(setTransportMarkedCheckedItem(markedItems));
      }
    });
  };

  const adjustWidthColumn = (name, width) => {
    const columnIndex = columnsWatch.current.findIndex((column) => column.name === name);
    const column = columnsWatch.current[columnIndex];
    const newWidth = width > column.maxWidth ? column.maxWidth : width < column.minWidth ? column.minWidth : width;
    const ul = columnsRefs.current[name].current?.parentElement?.parentElement?.parentElement;

    columnsRefs.current[name].current.parentElement.style.width = newWidth + 'px';
    columnsRefs.current[name].current.parentElement.style.minWidth = newWidth + 'px';

    for (var i = 0; i < ul?.children?.length; i++) {
      ul.children[i].children[columnIndex].style.width = newWidth + 'px';
      ul.children[i].children[columnIndex].style.minWidth = newWidth + 'px';
    }
  };

  const handleOnMouseMove = (e) => {
    if (isResizingColumn.current) {
      const newWidth = e.clientX - columnsRefs.current[isResizingColumn.current]?.current.parentElement?.getBoundingClientRect().left;
      adjustWidthColumn(isResizingColumn.current, newWidth);
    }
  };

  const handleOnMouseUp = () => {
    isResizingColumn.current = null;

    const columnsInfo = {};
    columns.forEach((column) => {
      const parentElement = columnsRefs.current[column.name]?.current?.parentElement;

      columnsInfo[column.name] = parentElement ? parseInt(parentElement.style.width.slice(0, -2)) : column.width;
    });

    setTransportColumnsWidth(columnsInfo);
  };

  const handleResizeColumn = (column, e) => {
    e.preventDefault();
    isResizingColumn.current = column.name;
  };

  const handleColumnRef = (column, el) => {
    if (el && !columnsRefs.current[column.name].current) {
      columnsRefs.current[column.name].current = el;
    }
  };

  useInterval(() => {
    if (moment().seconds() === 0) {
      setDatetimeInterval(moment());
    }
  }, 1000);

  useEffect(() => {
    document.onmousemove = handleOnMouseMove;
    document.onmouseup = handleOnMouseUp;

    return () => {
      document.onmousemove = null;
      document.onmouseup = null;
    };
  }, []);

  useEffect(() => {
    columnsWatch.current = columns;
  }, [columns]);

  useEffect(() => {
    setTransportCheckedStorageItems(transportCheckedItemsAll);
  }, [transportCheckedItemsAll]);

  useEffect(() => {
    setTransportMarkedCheckedStorageItems(transportMarkedCheckedItems);
  }, [transportMarkedCheckedItems]);

  useEffect(() => {
    setTransportProcessingCheckedStorageItems(transportProcessingCheckedItems);
  }, [transportProcessingCheckedItems]);

  useEffect(() => {
    setTransportSortCheckedStorageItems(transportSortSelectedItem);
  }, [transportSortSelectedItem]);

  useEffect(() => {
    switch (transportFilterCheckedType) {
      case 'filter':
        // Удалить из отфильтрованного списка уже отмеченные ТС.
        const sortItemsId = new Set(sortItems.map((item) => item.id));
        const checkedItems = new Set(transportMarkedCheckedItems);
        const newCheckedItems = Array.from(sortItemsId).filter((id) => !checkedItems.has(id));

        // Удалить из уже отмеченных ТС, если в отфильтрованном списке оно исчезло.
        const deleteMarkedItems = Array.from(checkedItems).filter((id) => !sortItemsId.has(id));
        const newMarkedItems = transportMarkedCheckedItems.filter((item) => !deleteMarkedItems.includes(item));

        batch(() => {
          dispatch(setTransportCheckedItem(newCheckedItems));
          dispatch(setTransportMarkedCheckedItem(newMarkedItems));
        });
        break;
      case 'all':
        break;
    }
  }, [sortItems, transportFilterCheckedType]);

  useEffect(() => {
    if (transportSelectedItem && listRef.current && searchItems.length > 0) {
      const selectedIndex = searchItems.findIndex((item) => item.id === transportSelectedItem.id);
      if (selectedIndex >= 0) {
        const el = document.querySelector(`[data-id="${transportSelectedItem.id}"]`);
        // проверка, что элемент не отрендерен и его нет в зоне видимости
        if (!el) {
          setTimeout(() => {
            listRef.current?.scrollToItem(selectedIndex, 'center');
          }, 100);
        }
      }
    }
  }, [transportSelectedItem?.id, transportSearchValue, transportSortSelectedItem]);

  return (
    <Box
      component='div'
      sx={{
        width: '100%',
        height: '100%',
        '*:focus': {
          outline: 'none',
        },
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        '.simplebar-container': {
          maxHeight: 'none',
          '.simplebar-horizontal': {
            right: '0px',
          },
          '.simplebar-vertical': {
            top: '30px',
            bottom: '0px',
          },
        },
      }}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <MapFilterTransportListContext.Provider
              value={{
                items: searchItems,
                width,
                height,
                columns,
                columnsRefs: columnsRefs.current,
                checkedItems: transportCheckedItems,
                searchValue: transportSearchValue,
                columnCheckedItems: transportColumnCheckedItems,
                sortSelectedItem: transportSortSelectedItem,
                onSortClick: handleSortClick,
                onCheckboxClick: handleCheckboxHeadClick,
                onClickResizeColumn: handleResizeColumn,
                onColumnRef: handleColumnRef,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    ref={listRef}
                    height={height}
                    innerElementType={mapFilterTransportInnerElementType}
                    itemCount={searchItems.length}
                    itemSize={24}
                    stickyIndices={[0]}
                    overscanCount={10}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    itemData={{
                      items: searchItems,
                      itemWidth: scrollableNodeRef?.current?.scrollWidth ?? width,
                      columns: columns,
                      datetimeInterval: datetimeInterval,
                      selectedItem: transportSelectedItem,
                      checkedItems: transportCheckedItems,
                      onClick: handleItemClick,
                      onDoubleClick: handleItemDoubleClick,
                      onCheckboxClick: handleCheckboxClick,
                      onColumnRef: handleColumnRef,
                    }}>
                    {MapFilterTransportListItem}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </MapFilterTransportListContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const mapFilterTransportInnerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <MapFilterTransportListContext.Consumer>
    {({ items, checkedItems, searchValue, onSortClick, onClickResizeColumn, sortSelectedItem, onCheckboxClick, columns, onColumnRef }) => {
      const checked = items.every((item) => checkedItems.includes(item.id));
      const indeterminate = items.some((item) => checkedItems.includes(item.id));

      return (
        <List ref={ref} style={{ ...style, minHeight: '100%' }} {...rest}>
          <ListItem
            disablePadding
            sx={(theme) => ({
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: style.height,
              minHeight: '100%',
              zIndex: '2',
              pointerEvents: 'none',
              padding: theme.spacing(0),
              '.MuiListItemText-root': {
                display: 'flex',
                justifyContent: 'space-between',
                height: '100%',
                position: 'relative',
                flex: 'none',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  height: '100%',
                  bgcolor: theme.palette.divider,
                  width: '1px',
                  zIndex: '1',
                  pointerEvents: 'none',
                },
                '&:last-of-type': {
                  flex: '1',
                  '&:before': {
                    display: 'none',
                  },
                },
              },
            })}>
            {columns.map((column) => (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText
                    style={{
                      minWidth: column.width ? column.width : column.minWidth,
                    }}
                  />
                )}
                renderForbidden={() => <React.Fragment />}
              />
            ))}
          </ListItem>
          <ListItem
            disablePadding
            sx={(theme) => ({
              position: 'sticky',
              top: '0',
              width: '100%',
              display: 'flex',
              zIndex: '2',
              '& > .MuiListItemButton-root': {
                '&:first-of-type': {
                  '.MuiListItemText-root': {
                    paddingLeft: '31px',
                  },
                },
              },
              '& > .MuiBox-root': {
                '&:last-of-type': {
                  flex: '1',
                  '.MuiListItemText-root': {
                    paddingRight: theme.spacing(2),
                  },
                },
              },
              '.MuiListItemButton-root': {
                display: 'flex',
                flex: 'none',
                height: '30px',
                padding: theme.spacing(0),
                position: 'relative',
                bgcolor: 'background.default',
                borderColor: theme.palette.divider,
                '.MuiTouchRipple-rippleVisible': {
                  color: 'inherit',
                },
                '.MuiListItemText-root': {
                  display: 'flex',
                  cursor: 'pointer',
                  margin: theme.spacing(0),
                  padding: '6px 6px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  svg: {
                    fontSize: '14px',
                  },
                  '.MuiTypography-root': {
                    display: 'flex',
                    '&:nth-of-type(1)': {
                      color: 'text.secondary',
                      marginRight: '4px',
                    },
                    '&:nth-of-type(2) svg': {
                      fontSize: '12px',
                      transition: 'all 300ms',
                      transform: 'rotateZ(0deg)',
                    },
                  },
                  '.MuiCheckbox-root': {
                    padding: theme.spacing(0),
                    marginLeft: '-2px',
                    svg: {
                      fontSize: '18px',
                    },
                    ...(searchValue.length !== 0 &&
                      items.length === 0 && {
                        display: 'none',
                      }),
                  },
                },
              },
            })}>
            {columns.slice(0, 1).map((column) => (
              <ListItemButton
                key={column.name}
                disableTouchRipple
                style={{
                  minWidth: column.width ? column.width : column.minWidth,
                }}>
                <ListItemText disableTypography>
                  <Checkbox
                    size='small'
                    color='primary'
                    checked={checked}
                    indeterminate={!checked && indeterminate}
                    onClick={onCheckboxClick}
                    disableRipple></Checkbox>
                </ListItemText>
              </ListItemButton>
            ))}
            {columns.slice(1).map((column) => (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <Box
                    component='div'
                    sx={{
                      display: 'flex',
                      position: 'relative',
                      minWidth: column.width ? column.width : column.minWidth,
                    }}>
                    <ListItemButton
                      onClick={() => onSortClick(column)}
                      disableTouchRipple={!column.order}
                      sx={(theme) => ({
                        flex: '1 1 auto !important',
                        '.MuiTypography-root': {
                          width: 'auto',
                          '&:nth-of-type(2)': {
                            opacity: '0.2',
                            transform: 'rotateZ(0)',
                            transition:
                              'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                            ...(sortSelectedItem.name === column.name && {
                              opacity: '1',
                              ...(sortSelectedItem.order === 'DESC' && {
                                transform: 'rotateZ(180deg)',
                              }),
                            }),
                          },
                        },
                      })}>
                      <ListItemText disableTypography>
                        <Typography>{column.icon ? <MapTransportIcon name={column.icon} /> : column.label}</Typography>
                        <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
                      </ListItemText>
                    </ListItemButton>
                    <Box
                      component='div'
                      ref={(el) => onColumnRef(column, el)}
                      onMouseDown={(e) => onClickResizeColumn(column, e)}
                      sx={(theme) => ({
                        minWidth: '4px',
                        minHeight: '30px',
                        cursor: 'col-resize',
                        backgroundColor: theme.palette.background.default,
                        '&:hover, &:active': {
                          backgroundColor: alpha(theme.palette.text.secondary, 0.12),
                        },
                      })}
                    />
                  </Box>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            ))}
          </ListItem>
          {children}
        </List>
      );
    }}
  </MapFilterTransportListContext.Consumer>
));

const MapFilterTransportListItem = ({ data, index, style }) => {
  const { items, selectedItem, onClick, datetimeInterval, onDoubleClick, onCheckboxClick, checkedItems, columns } = data;

  return (
    <ListItem
      key={index}
      style={style}
      disablePadding
      data-id={items[index].id}
      selected={selectedItem?.id === items[index].id}
      onClick={() => onClick(items[index])}
      onDoubleClick={() => onDoubleClick(items[index])}
      sx={(theme) => ({
        width: '100%',
        marginTop: '30px',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          overflow: 'hidden',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            gap: '3px',
            svg: {
              fontSize: '16px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
          '.MuiCheckbox-root': {
            padding: theme.spacing(0),
            marginLeft: '-2px',
            svg: {
              fontSize: '18px',
            },
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
      })}>
      <ListItemText disableTypography style={{ minWidth: '38px' }}>
        {
          {
            circle: <TransportCircleIcon color={items[index].color} />,
            triangle: <TransportTriangleIcon color={items[index].color} />,
            square: <TransportSquareIcon color={items[index].color} />,
            null: <TransportCircleIcon sx={{ opacity: 0 }} />,
          }[items[index].group_type]
        }
        <Checkbox
          size='small'
          color='primary'
          checked={checkedItems.indexOf(items[index].id) !== -1}
          onClick={onCheckboxClick(items[index])}
          disableRipple></Checkbox>
      </ListItemText>
      {columns.map((column) => {
        const width = column.width ? column.width : column.minWidth;
        const condition = items[index].conditions[items[index].conditions.length - 1]?.data;

        switch (column.name) {
          case 'name':
            return (
              <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                <Typography>{items[index].name}</Typography>
              </ListItemText>
            );
          case 'number':
            return (
              <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                <Typography>{items[index].number}</Typography>
              </ListItemText>
            );
          case 'condition':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText
                    key={column.name}
                    disableTypography
                    sx={(theme) => ({
                      width,
                      minWidth: width,
                      div: {
                        display: 'flex',
                        marginRight: '2px',
                        svg: {
                          fontSize: '18px',
                        },
                      },
                      'div:last-of-type': {
                        marginRight: theme.spacing(0),
                      },
                    })}>
                    <MapTransportConditionIconsList
                      history={false}
                      zoom={false}
                      datetime={datetimeInterval}
                      data={items[index]}
                      conditions={items[index].conditions}
                    />
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
          case 'geofence':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                    <Typography>
                      {condition && condition.geofence_id !== config.conditions.geofence_id.default ? condition.geofence_name : ''}
                    </Typography>
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
          case 'total_mileage_avg':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                    {config.transportDeviceTypes[items[index].device_type] === 'galileosky' ? (
                      <Typography>{condition ? Math.round(condition.total_mileage_avg / 1000).toLocaleString('ru-RU') : ''}</Typography>
                    ) : (
                      <Typography />
                    )}
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
          case 'parking_time':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                    {config.transportDeviceTypes[items[index].device_type] === 'galileosky' ? (
                      <Typography>
                        {condition && condition.parking_time !== config.conditions.parking_time.default
                          ? Math.round(condition.parking_time / 60)
                          : ''}
                      </Typography>
                    ) : (
                      <Typography />
                    )}
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
          case 'sensor_count':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                    {config.transportDeviceTypes[items[index].device_type] === 'galileosky' ? (
                      <Typography>
                        {condition && condition.sensor_count !== config.conditions.sensor_count.default ? condition.sensor_count : ''}
                      </Typography>
                    ) : (
                      <Typography />
                    )}
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
          case 'signal_off':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                    {config.transportDeviceTypes[items[index].device_type] === 'galileosky' ? (
                      <Typography>
                        {items[index] > config.signalOffTimeout[0].interval[1] ? Math.round(items[index].signal_off / 1000 / 60) : ''}
                      </Typography>
                    ) : (
                      <Typography />
                    )}
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
          case 'guard_status_sum':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText key={column.name} disableTypography style={{ width, minWidth: width }}>
                    {config.transportDeviceTypes[items[index].device_type] === 'galileosky' ? (
                      <Typography>
                        {condition
                          ? config.numberOfTank
                              .map((number) => condition[`dut${number}`])
                              .filter((number) => number !== config.conditions.dut.default)
                              .reduce((a, b) => a + b, 0)
                          : ''}
                      </Typography>
                    ) : (
                      <Typography />
                    )}
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
          case 'last_activity_date':
            return (
              <Protect
                key={column.name}
                permissions={column.permissions}
                renderPermitted={() => (
                  <ListItemText disableTypography style={{ width, minWidth: width }}>
                    <Typography>
                      {items[index].last_activity_date
                        ? Math.max(Math.round(moment(datetimeInterval).diff(moment(items[index].last_activity_date)) / 1000 / 60), 0)
                        : ''}
                    </Typography>
                  </ListItemText>
                )}
                renderForbidden={() => <React.Fragment />}
              />
            );
        }
      })}
    </ListItem>
  );
};

export default MapFilterTransportList;
